import { useState, useEffect, useRef, useCallback } from "react";
import { getUtilityConnectionStatus } from "services/utilityConnectionStatus";

const FINISHED_STATUSES = ["ConnectedDataIncomplete", "ConnectedDataComplete"];

const useGetUtilityConnectionStatus = (
  hybridToken: string,
  prospectId: string | null,
  pollInterval: number = 15000 // Default 15 seconds
) => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [status, setStatus] = useState<string | null>();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const pollStatus = useCallback(async () => {
    if (!hybridToken || !prospectId) return;

    try {
      const response = await getUtilityConnectionStatus(
        hybridToken,
        prospectId
      );

      setStatus(response.statuses[0].overallStatus);
      setError(null);
    } catch (error) {
      setError(error as Error);
    }
  }, [hybridToken, prospectId]);

  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    setInitialLoading(true);

    // Initial call
    pollStatus();
    setInitialLoading(false);

    // Set up polling
    intervalRef.current = setInterval(() => {
      setLoading(true);
      pollStatus().finally(() => setLoading(false));
    }, pollInterval);
  }, [pollInterval, pollStatus]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (status && FINISHED_STATUSES.includes(status)) {
      stopPolling();
    }
  }, [status, stopPolling]);

  return {
    loading,
    initialLoading,
    error,
    status,
    startPolling,
    stopPolling,
  };
};

export { useGetUtilityConnectionStatus };
